<script setup lang="ts">
import AppStepper from '@core/components/AppStepper.vue'
import SearchableSelectList, { type ISearchableSelectItem } from '@/components/createdUi/SearchableSelectList.vue'
import { useTableItemsPerPage } from '@/composables/useTableItemsPerPage'
import { useFeedbackStore } from '@/modules/feedback/stores/feedbackStore'
import { useFilesStore } from '@/modules/files/stores/filesStore'
import { setImportHeaderBack } from '../importHeaderBack'
import { useImportStore } from '../stores/importStore'
import type { IImportCellCorrection } from '../types/IImportCellCorrection'
import type { IImportColumnSelection } from '../types/IImportColumnSelection'
import type { IImportExtraParam } from '../types/IImportSchema'
import type { IImportRowsResponse } from '../types/IImportRowsResponse'
import { IMPORT_ROW_FILTER_TO_STATUS, type TImportRowFilter } from '../types/TImportRowFilter'
import type { IImportUploadResponse } from '../types/IImportUploadResponse'
import type { IImportValidateResponse } from '../types/IImportValidateResponse'
import ImportActiveProgress from './ImportActiveProgress.vue'
import ImportColumnMapping from './ImportColumnMapping.vue'
import ImportExampleModal from './ImportExampleModal.vue'
import ImportFileDrop from './ImportFileDrop.vue'
import ImportHistoryModal from './ImportHistoryModal.vue'
import ImportPreviewStep from './ImportPreviewStep.vue'

const route = useRoute()
const router = useRouter()
const importStore = useImportStore()
const feedbackStore = useFeedbackStore()
const filesStore = useFilesStore()

const zipOnly = computed(() => route.query.zipOnly === '1')

const steps = computed(() => zipOnly.value
  ? [
      { title: 'Загрузка файла' },
      { title: 'Проверка данных' },
    ]
  : [
      { title: 'Загрузка файла' },
      { title: 'Проверка структуры' },
      { title: 'Проверка данных' },
    ])

const previewStep = computed(() => zipOnly.value ? 2 : 3)

/** Дополнительные поля схемы только у импорта нормативов. */
const normativeExtraParams = computed(() =>
  importType.value === 'qualitycontrol' ? importStore.extraParams : [],
)

const step = ref(1)
const exampleOpen = ref(false)
const historyOpen = ref(false)
const historyFullscreen = ref(false)
const showActiveProgress = ref(false)
const localFile = ref<File | null>(null)
const selections = ref<IImportColumnSelection[]>([])
const fieldErrors = ref<Record<number, string>>({})
const busy = ref(false)
const rowFilter = ref<TImportRowFilter>('all')
const corrections = ref<IImportCellCorrection[]>([])
const extraValues = ref<Record<string, string>>({})
const extraFieldErrors = ref<Record<string, string>>({})
const openExtraName = ref<string | null>(null)
const extraMenuWidth = ref<Record<string, number>>({})
const { itemsPerPage } = useTableItemsPerPage('importRows', 25)
if (![25, 50, 100].includes(itemsPerPage.value))
  itemsPerPage.value = 25

const importType = computed(() => String(route.params.importType || ''))

const accept = computed(() => importStore.acceptedExtensions.join(','))

function leaveWizard() {
  const from = route.query.from
  if (typeof from === 'string' && from.startsWith('/')) {
    router.push(from)
    return
  }
  if (window.history.state?.back != null)
    router.back()
}

function openHistory(fullscreen = false) {
  historyFullscreen.value = fullscreen
  historyOpen.value = true
}

async function onResumeDraft(uuid: string) {
  historyOpen.value = false
  const resumed = await importStore.resumeImportDraft(uuid)
  if (!resumed) return
  if (importStore.upload) {
    selections.value = selectionsFromUpload(importStore.upload)
  }
  if (resumed.step === 'preview') {
    step.value = previewStep.value
  } else {
    step.value = 2
  }
}

function onNewImport() {
  showActiveProgress.value = false
  resetFile()
}

async function onCancelTask(id: number) {
  await importStore.cancelImportById(importType.value, id)
}

function onHeaderBack() {
  if (showActiveProgress.value) {
    showActiveProgress.value = false
    resetFile()
    return
  }
  if (step.value > 1) {
    step.value -= 1
    return
  }
  leaveWizard()
}

function resetFile() {
  localFile.value = null
  selections.value = []
  fieldErrors.value = {}
  rowFilter.value = 'all'
  corrections.value = []
  importStore.clearSession()
  step.value = 1
}


function applyExtraDefaults() {
  const next: Record<string, string> = {}
  for (const param of normativeExtraParams.value) {
    next[param.name] = extraValues.value[param.name] || param.default || ''
  }
  extraValues.value = next
}

function missingExtra() {
  return normativeExtraParams.value.filter(param => param.required && !String(extraValues.value[param.name] || '').trim())
}

function rejectMissingExtra() {
  const missing = missingExtra()
  if (!missing.length) {
    extraFieldErrors.value = {}
    return false
  }
  const errors: Record<string, string> = {}
  for (const param of missing)
    errors[param.name] = 'Выберите значение'
  extraFieldErrors.value = errors
  feedbackStore.showToast({
    type: 'error',
    message: `Выберите: ${missing.map(param => param.label).join(', ')}`,
    time: 5000,
  })
  return true
}

function extraItems(param: IImportExtraParam): ISearchableSelectItem[] {
  return (param.choices || []).map(choice => ({ id: choice.value, label: choice.label }))
}

function extraLabel(name: string) {
  const param = normativeExtraParams.value.find(item => item.name === name)
  const value = extraValues.value[name]
  return param?.choices.find(choice => String(choice.value) === String(value))?.label || ''
}

function bindExtraField(name: string) {
  return (el: unknown) => {
    const node = el && typeof el === 'object' && '$el' in el
      ? (el as { $el: HTMLElement }).$el
      : el as HTMLElement | null
    if (node?.offsetWidth)
      extraMenuWidth.value[name] = node.offsetWidth
  }
}

function setExtraMenu(name: string, open: boolean) {
  openExtraName.value = open ? name : (openExtraName.value === name ? null : openExtraName.value)
}

function onExtraSelect(name: string, item: ISearchableSelectItem) {
  extraValues.value[name] = String(item.id)
  openExtraName.value = null
  const next = { ...extraFieldErrors.value }
  delete next[name]
  extraFieldErrors.value = next
}

function onExtraClear(name: string) {
  extraValues.value[name] = ''
}

async function loadRows(page = 1) {
  const fileUuid = importStore.upload?.uuid
  if (!fileUuid)
    return null
  return importStore.fetchImportRows(
    fileUuid,
    IMPORT_ROW_FILTER_TO_STATUS[rowFilter.value],
    page,
    itemsPerPage.value,
  )
}

function validationFromRows(data: IImportRowsResponse): IImportValidateResponse {
  return {
    is_valid: data.error_rows_count === 0,
    total_rows: data.total_rows,
    valid_rows_count: data.valid_rows_count,
    error_rows_count: data.error_rows_count,
    warning_rows_count: data.warning_rows_count,
    skipped_rows_count: data.skipped_rows_count ?? 0,
    errors: [],
    warnings: [],
    skipped: data.skipped ?? [],
    columns_summary: {},
    preview_rows: [],
    uuid: data.uuid,
    file_name: data.file_name,
  }
}

function selectionsFromUpload(upload: IImportUploadResponse): IImportColumnSelection[] {
  const selectedByHeader = new Map<string, string>()
  for (const [canonical, header] of Object.entries(upload.mapping)) {
    if (!selectedByHeader.has(header))
      selectedByHeader.set(header, canonical)
  }
  return upload.file_columns.map(column => ({
    header: column.name,
    canonical: selectedByHeader.get(column.name) ?? null,
  }))
}

function extensionOf(name: string) {
  const dot = name.lastIndexOf('.')
  return dot >= 0 ? name.slice(dot).toLowerCase() : ''
}

function columnMapping() {
  const mapping: Record<string, string> = {}
  for (const item of selections.value) {
    if (item.canonical)
      mapping[item.canonical] = item.header
  }
  return mapping
}

async function onFile(file: File) {
  if (busy.value || !importType.value)
    return
  const ext = extensionOf(file.name)
  if (!importStore.acceptedExtensions.includes(ext)) {
    feedbackStore.showToast({
      type: 'error',
      message: `Формат не поддерживается. Допустимо: ${importStore.acceptedExtensions.join(', ')}`,
      time: 5000,
    })
    return
  }
  if (rejectMissingExtra())
    return
  busy.value = true
  try {
    const uploaded = await importStore.uploadImportFile(importType.value, file)
    if (!uploaded)
      return
    localFile.value = file
    selections.value = selectionsFromUpload(uploaded)
    fieldErrors.value = {}
    if (!zipOnly.value) {
      step.value = 2
      return
    }
    corrections.value = []
    rowFilter.value = 'all'
    const rows = await loadRows(1)
    if (!rows)
      return
    importStore.validation = validationFromRows(rows)
    step.value = previewStep.value
  } finally {
    busy.value = false
  }
}

function onSelections(next: IImportColumnSelection[]) {
  selections.value = next
  fieldErrors.value = {}
}

function missingRequired() {
  const selected = new Set(selections.value.map(item => item.canonical).filter(Boolean))
  return (importStore.upload?.required_columns ?? []).filter(column => column.required && !selected.has(column.name))
}

async function onContinue() {
  const upload = importStore.upload
  if (!upload || busy.value || !importType.value)
    return
  if (rejectMissingExtra()) {
    step.value = 1
    return
  }
  const missing = missingRequired()
  if (missing.length) {
    const errors: Record<number, string> = {}
    for (const column of missing) {
      const header = upload.mapping[column.name]
      const index = selections.value.findIndex(item => item.header === header && !item.canonical)
      if (index >= 0)
        errors[index] = 'Выберите поле из списка'
    }
    fieldErrors.value = errors
    feedbackStore.showToast({
      type: 'error',
      message: `Выберите обязательные поля: ${missing.map(column => column.name).join(', ')}`,
      time: 5000,
    })
    return
  }
  busy.value = true
  try {
    corrections.value = []
    const result = await importStore.validateImport(
      importType.value,
      upload.uuid,
      columnMapping(),
      [],
      extraValues.value,
    )
    if (!result)
      return
    rowFilter.value = 'all'
    await loadRows(1)
    step.value = previewStep.value
  } finally {
    busy.value = false
  }
}

async function onSave() {
  const upload = importStore.upload
  const validation = importStore.validation
  if (!upload || !validation || busy.value || !importType.value)
    return
  if (rejectMissingExtra()) {
    step.value = 1
    return
  }
  busy.value = true
  try {
    const saved = await importStore.executeImport(
      importType.value,
      upload.uuid,
      columnMapping(),
      validation.error_rows_count > 0,
      corrections.value,
      extraValues.value,
    )
    if (saved) {
      showActiveProgress.value = !importType.value.startsWith('ep_')
      resetFile()
    }
  } finally {
    busy.value = false
  }
}

async function onCellEdit(payload: IImportCellCorrection) {
  const correction: IImportCellCorrection = {
    row: Number(payload.row),
    column: String(payload.column),
    value: payload.value == null ? '' : String(payload.value),
  }
  const rest = corrections.value.filter(item => !(item.row === correction.row && item.column === correction.column))
  corrections.value = [...rest, correction]
  const upload = importStore.upload
  if (!upload || !importType.value)
    return
  busy.value = true
  try {
    const result = await importStore.validateImport(
      importType.value,
      upload.uuid,
      columnMapping(),
      corrections.value.map(item => ({ ...item })),
      extraValues.value,
    )
    if (!result)
      return
    // В текущем фильтре строк не осталось — возвращаемся ко всем строкам
    const filterCount: Record<TImportRowFilter, number> = {
      all: result.total_rows,
      errors: result.error_rows_count,
      warnings: result.warning_rows_count,
      skipped: result.skipped_rows_count ?? 0,
    }
    const isFilterEmpty = filterCount[rowFilter.value] === 0
    if (isFilterEmpty)
      rowFilter.value = 'all'
    await loadRows(isFilterEmpty ? 1 : importStore.rowsPage)
  } finally {
    busy.value = false
  }
}

async function onRowFilter(value: TImportRowFilter) {
  if (rowFilter.value === value)
    return
  rowFilter.value = value
  await loadRows(1)
}

async function onRowPage(page: number) {
  if (page === importStore.rowsPage)
    return
  await loadRows(page)
}

async function onRowPageSize(size: number) {
  if (![25, 50, 100].includes(size) || size === itemsPerPage.value)
    return
  itemsPerPage.value = size
  await loadRows(1)
}

watch(importType, type => {
  resetFile()
  showActiveProgress.value = false
  extraValues.value = {}
  extraFieldErrors.value = {}
  openExtraName.value = null
  if (!type) {
    importStore.activeTasks = []
    importStore.activeImport = null
    importStore.activeImportChecked = true
    return
  }
  if (type.startsWith('ep_')) {
    importStore.activeImportType = type
    importStore.activeTasks = []
    importStore.activeImport = null
    importStore.activeImportChecked = true
  } else {
    importStore.watchActiveImport(type).then(() => {
      if (importStore.activeTasks.some(t => t.status === 'in_progress')) {
        showActiveProgress.value = true
      }
    })
  }
  importStore.fetchColumns(type).then(ok => {
    if (ok)
      applyExtraDefaults()
  })
  importStore.fetchImportTypes()
}, { immediate: true })

async function refreshActiveTasks() {
  if (!importType.value || importType.value.startsWith('ep_')) return
  await Promise.all([
    importStore.fetchActiveImport(importType.value),
    importStore.fetchUnfinishedImports(importType.value),
  ])
}

watch(step, (newStep, oldStep) => {
  if (newStep === 1 && oldStep && oldStep > 1) {
    void refreshActiveTasks()
  }
})

watch(() => importStore.activeTasks.length, newLen => {
  if (showActiveProgress.value && newLen === 0) {
    showActiveProgress.value = false
    resetFile()
  }
})

setImportHeaderBack(onHeaderBack)

onBeforeUnmount(() => {
  setImportHeaderBack(null)
  importStore.clearSession()
  importStore.activeTasks = []
  importStore.activeImport = null
  importStore.activeImportChecked = false
})

</script>

<template>
  <div
    class="d-flex flex-column ga-6"
    :class="{ 'import-preview-page': step === previewStep }"
  >
    <ImportActiveProgress
      v-if="showActiveProgress"
      :active-tasks="importStore.activeTasks"
      :import-type="importType"
      @cancel-task="onCancelTask"
      @new-import="onNewImport"
    />
    <template v-else>
    <div
      class="import-stepper-row d-flex align-center justify-space-between ga-3"
    >
      <AppStepper
        class="import-stepper"
        :items="steps"
        :current-step="step - 1"
        :is-active-step-valid="true"
        align="start"
      />
      <VBtn
        variant="outlined"
        color="secondary"
        prepend-icon="ri-history-line"
        @click="openHistory(false)"
      >
        История импорта
      </VBtn>
    </div>

    <div v-if="step === 1" class="d-flex flex-column ga-6 import-step-upload">
      <VAlert
        color="info"
        variant="tonal"
        icon="ri-question-line"
        class="cursor-pointer import-prepare-alert"
        @click="exampleOpen = true"
      >
        Как подготовить таблицу для загрузки
      </VAlert>
      <VMenu
        v-for="param in normativeExtraParams"
        :key="param.name"
        :model-value="openExtraName === param.name"
        :close-on-content-click="false"
        :max-height="350"
        :width="extraMenuWidth[param.name]"
        location="bottom"
        content-class="entity-form-menu"
        :disabled="busy"
        @update:model-value="setExtraMenu(param.name, $event)"
      >
        <template #activator="{ props: menuProps }">
          <VTextField
            :ref="bindExtraField(param.name)"
            :model-value="extraLabel(param.name)"
            class="invisible-readonly cursor-pointer"
            :append-inner-icon="openExtraName === param.name ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'"
            variant="outlined"
            density="comfortable"
            :placeholder="param.label"
            persistent-placeholder
            readonly
            clearable
            :error="Boolean(extraFieldErrors[param.name])"
            :error-messages="extraFieldErrors[param.name]"
            :hint="extraFieldErrors[param.name] ? undefined : param.description"
            :persistent-hint="!extraFieldErrors[param.name]"
            :disabled="busy"
            v-bind="menuProps"
            @click:append-inner.stop="setExtraMenu(param.name, openExtraName !== param.name)"
            @click:clear="onExtraClear(param.name)"
          >
            <template #label>
              <span>{{ param.label }}</span>
            </template>
          </VTextField>
        </template>
        <SearchableSelectList
          :items="extraItems(param)"
          placeholder="Поиск..."
          @select="onExtraSelect(param.name, $event)"
        />
      </VMenu>
      <ImportFileDrop
        :accept="accept"
        :disabled="busy || !importStore.acceptedExtensions.length"
        :progress="filesStore.uploadState.active ? filesStore.uploadState.pct : null"
        :file-name="filesStore.uploadState.active ? filesStore.uploadState.files[0]?.name : null"
        @file="onFile"
      />
      <div
        v-if="importStore.unfinishedCount > 0"
        class="import-unfinished-alert cursor-pointer pa-4 rounded-lg d-flex align-start ga-3"
        @click="openHistory(true)"
      >
        <VIcon icon="ri-time-line" size="24" class="unfinished-icon" />
        <div class="d-flex flex-column">
          <span class="unfinished-title">Есть незавершённые импорты</span>
          <span class="unfinished-subtitle">Вы можете продолжить или отменить процессы импорта в истории импорта</span>
        </div>
      </div>
    </div>

    <ImportColumnMapping
      v-else-if="!zipOnly && step === 2 && importStore.upload"
      :file-name="localFile?.name || importStore.upload.file_name"
      :file-size="localFile?.size || 0"
      :upload="importStore.upload"
      :selections="selections"
      :errors="fieldErrors"
      @update:selections="onSelections"
      @remove="resetFile"
      @back="step = 1"
      @next="onContinue"
    />

    <ImportPreviewStep
      v-else-if="step === previewStep && importStore.upload && importStore.validation"
      :columns="importStore.columns"
      :file-name="localFile?.name || importStore.upload.file_name"
      :file-size="localFile?.size || 0"
      :validation="importStore.validation"
      :selections="selections"
      :rows="importStore.rows"
      :page="importStore.rowsPage"
      :total-pages="importStore.rowsTotalPages"
      :page-size="itemsPerPage"
      :rows-count="importStore.rowsCount"
      :filter="rowFilter"
      :saving="busy"
      :structure="importStore.rowsStructure"
      :paginated="importStore.rowsPaginated"
      :rows-notice="importStore.rowsNotice"
      :allow-cell-edit="!zipOnly"
      @remove="resetFile"
      @back="step = previewStep - 1"
      @save="onSave"
      @update:filter="onRowFilter"
      @update:page="onRowPage"
      @update:page-size="onRowPageSize"
      @edit-cell="onCellEdit"
    />
    </template>

    <ImportExampleModal
      v-model="exampleOpen"
      :columns="importStore.columns"
      :formats="importStore.acceptedExtensions"
    />
    <ImportHistoryModal
      v-model="historyOpen"
      :import-type="importType"
      @resume="onResumeDraft"
    />

  </div>
</template>

<style lang="scss">
.layout-content-wrapper:has(.import-preview-page) {
  max-block-size: 100dvh;
}

.layout-page-content:has(.import-preview-page) {
  display: flex;
  flex-direction: column;
  min-block-size: 0;
  overflow: hidden;

  .page-content-container {
    display: flex;
    flex: 1;
    flex-direction: column;
    min-block-size: 0;
    overflow: hidden;

    *:has(.import-preview-page) {
      display: flex;
      flex: 1;
      flex-direction: column;
      min-block-size: 0;
    }
  }
}
</style>

<style scoped lang="scss">
.import-step-upload {
  max-width: 700px;
}

.import-unfinished-alert {
  background-color: rgba(var(--v-theme-warning), var(--v-selected-opacity)) !important;
  border-radius: 8px;

  .unfinished-icon {
    color: rgb(var(--v-theme-warning)) !important;
    flex-shrink: 0;
    margin-block-start: 2px;
  }

  .unfinished-title {
    color: rgb(var(--v-theme-warning)) !important;
    font-family: 'Inter', sans-serif;
    font-size: 18px;
    font-style: normal;
    font-weight: 500;
    line-height: 28px;
  }

  .unfinished-subtitle {
    color: rgb(var(--v-theme-warning)) !important;
    font-feature-settings: 'liga' off, 'clig' off;
    font-family: 'Inter', sans-serif;
    font-size: 15px;
    font-style: normal;
    font-weight: 400;
    line-height: 20px;
  }
}

.import-preview-page {
  flex: 1;
  min-block-size: 0;
  overflow: hidden;

  > .import-stepper-row {
    flex-shrink: 0;
  }
}

/* Маска иконки красится background-color. Тема ставит белый color, из-за этого круг белый. */
.import-prepare-alert.v-alert {
  background: rgba(var(--v-theme-info), var(--v-selected-opacity)) !important;
}

.import-prepare-alert.v-alert:deep(.v-alert__prepend) {
  background: transparent !important;
  block-size: auto !important;
  border-radius: 0 !important;
  inline-size: auto !important;
}

.import-prepare-alert.v-alert.v-alert--variant-tonal.text-info:deep(.v-alert__prepend .v-icon) {
  background-color: rgb(var(--v-theme-info)) !important;
  block-size: 1.375rem !important;
  color: rgb(var(--v-theme-info)) !important;
  font-size: 1.375rem !important;
  inline-size: 1.375rem !important;
}

.import-stepper {
  :deep(.step-title),
  :deep(.step-number) {
    font-family: 'Inter', sans-serif;
    font-size: 0.938rem;
    font-weight: 500;
    letter-spacing: 0;
    line-height: 1.375rem;
  }

  :deep(.stepper-steps-active .step-title),
  :deep(.stepper-steps-active .step-number),
  :deep(.stepper-steps-completed .step-title),
  :deep(.stepper-steps-completed .step-number) {
    color: rgba(var(--v-theme-text-primary), var(--v-high-emphasis-opacity));
  }

  :deep(.stepper-steps-active .stepper-step-indicator) {
    border-color: rgb(var(--v-theme-primary));
    opacity: 1;
  }

  :deep(.app-stepper-step:not(.stepper-steps-active, .stepper-steps-completed)) {
    .step-title,
    .step-number {
      color: rgba(var(--v-theme-on-surface), var(--v-disabled-opacity));
    }

    .stepper-step-indicator {
      border-color: rgba(var(--v-theme-on-surface), var(--v-disabled-opacity));
      opacity: 1;
    }
  }
}
</style>
